import type { Building } from "building-types/building/buildingTypes";
import {
  createContextState,
  createContextStateProvider,
  createUseContextState,
  createUseContextStateRef,
  createUseContextStateSelector,
  createUseSetContextState,
  createUseSetContextStatePartial,
} from "ui/hooks/useContextState";
import type { ShowroomRenderingState, ShowroomState } from "./showroomState";
import { defaultShowroomState } from "./showroomState";

export const ShowroomContext = createContextState<ShowroomState>(defaultShowroomState, "ShowroomContext");
export const ShowroomContextStateProvider = createContextStateProvider<ShowroomState>(
  ShowroomContext,
  defaultShowroomState,
);
export const ShowroomEditProvider = createContextStateProvider<ShowroomState>(ShowroomContext, {
  ...defaultShowroomState,
  mode: "EDIT",
});
export const ShowroomOpsProvider = createContextStateProvider<ShowroomState>(ShowroomContext, {
  ...defaultShowroomState,
  showroomVersion: "ops",
});
export const ShowroomSalesProvider = createContextStateProvider<ShowroomState>(ShowroomContext, {
  ...defaultShowroomState,
  showroomVersion: "sales",
});
export const useSetShowroomState = createUseSetContextState<ShowroomState>(ShowroomContext);
export const useSetShowroomStatePartial = createUseSetContextStatePartial<ShowroomState>(ShowroomContext);
export const useShowroomStateRef = createUseContextStateRef<ShowroomState>(ShowroomContext);
const useShowroomStateSelector = createUseContextStateSelector<ShowroomState>(ShowroomContext);
const useShowroomState = createUseContextState<ShowroomState>(ShowroomContext);
export default useShowroomState;

const LOADING_OPTION_KEYS: readonly (keyof ShowroomState)[] = ["loadingOption"];

export function useIsShowroomOptionLoading(optionSetId: string, optionId: string): boolean {
  return useShowroomStateSelector(
    LOADING_OPTION_KEYS,
    (state) => state.loadingOption?.optionSetId === optionSetId && state.loadingOption.optionId === optionId,
  );
}

export function useShowroomPendingOptionId(optionSetId: string): string | undefined {
  return useShowroomStateSelector(LOADING_OPTION_KEYS, (state) => {
    if (state.loadingOption?.optionSetId !== optionSetId) {
      return undefined;
    }
    return state.loadingOption.activeOptionId ?? state.loadingOption.optionId;
  });
}

/** Extract building from rendering state when in building mode, undefined otherwise. */
export function getBuildingFromRenderState(renderingState: ShowroomRenderingState): Building | undefined {
  return renderingState.renderMode === "building" ? renderingState.building : undefined;
}
